import { X } from 'lucide-react';
import type { PlayerPublic } from '@ringmaster/shared';
import { MutedMic } from '../discord/voice/VoiceIndicators.js';
import { LevelDiamond } from './LevelDiamond.js';
import { Glyph } from './Glyph.js';

/**
 * RosterList — the joined-players list used in BOTH the Hand lobby and the
 * Host/Stage lobby. Extracted from the Hand lobby (the design the user prefers):
 * a titled card with a live count pill and a per-player row showing a
 * connection dot, the name, and a "you" marker for the current player.
 *
 * The Host lobby passes `myName={undefined}` (no "you" highlight on the TV),
 * the Hand lobby passes the player's own name to highlight their row.
 *
 * When `onKick` is provided (host lobby only) each row that isn't the host's own
 * seat (`myId`) gets a small remove button — a recovery tool for a wedged phone.
 */
export interface RosterListProps {
  players: PlayerPublic[];
  minPlayers: number;
  /** The current player's name — highlights their row + shows "you". */
  myName?: string;
  /** The current player's id — used to hide the kick button on the host's own row. */
  myId?: string;
  /** Host-only: remove a player from the lobby. Renders a per-row kick button. */
  onKick?: (playerId: string) => void;
}

export function RosterList({ players, minPlayers, myName, myId, onKick }: RosterListProps) {
  const living = players.filter((p) => p.alive);
  // After a rematch, cumulative match points show next to each name.
  const hasScores = living.some((p) => p.score > 0);

  return (
    <div
      style={{
        background: 'rgba(0,0,0,0.4)',
        border: '1px solid var(--surface-stroke)',
        borderRadius: 'var(--radius-card)',
        padding: 'var(--space-5)',
        width: '100%',
        textAlign: 'left',
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 'var(--space-4)',
        }}
      >
        <span style={{ color: 'var(--text-secondary)', fontSize: 13, fontWeight: 500 }}>
          Players joined
        </span>
        <span
          style={{
            background: 'rgba(126,92,68,0.3)',
            color: 'var(--text-secondary)',
            borderRadius: 'var(--radius-pill)',
            padding: '4px 10px',
            fontSize: 12,
            fontWeight: 600,
          }}
        >
          {living.length} / {minPlayers}+
        </span>
      </div>
      <ul
        style={{
          listStyle: 'none',
          margin: 0,
          padding: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--space-2)',
        }}
      >
        {living.map((p) => {
          const isMe = myName != null && p.name === myName;
          return (
            <li
              key={p.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--space-3)',
                padding: '10px var(--space-3)',
                borderRadius: 12,
                background: isMe ? 'rgba(126,92,68,0.15)' : 'transparent',
                border: isMe ? '1px solid rgba(126,92,68,0.4)' : '1px solid transparent',
              }}
            >
              {/* An AI seat has no socket, so the presence dot has nothing true
                  to say — green would claim it is online and grey would claim
                  it dropped. It wears the SAME troupe mask the medallion grid
                  puts on it instead (one mark for one thing). */}
              {p.isAi ? (
                <span style={{ display: 'flex', flexShrink: 0 }}>
                  <Glyph name="mask" tone="arcana" size={12} />
                </span>
              ) : (
                <div
                  style={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    background: p.connected ? '#4ade80' : 'var(--text-tertiary)',
                    flexShrink: 0,
                  }}
                />
              )}
              {/* No avatars on this list, so the level rides inline just before
                  the name — same diamond, just sized to the text. */}
              <LevelDiamond level={p.level} size={18} />
              <span style={{ fontSize: 15, color: 'var(--text-primary)' }}>{p.name}</span>
              {isMe && (
                <span style={{ color: 'var(--text-tertiary)', fontSize: 12 }}>you</span>
              )}
              {/* AI seats are labelled everywhere they appear — same word, same
                  weight as the "you" tag beside it (AI_SEATS_SPEC.md §6). */}
              {p.isAi && (
                <span style={{ color: 'var(--text-tertiary)', fontSize: 12 }}>AI</span>
              )}
              <MutedMic discordUserId={p.discordUserId} size={13} />
              {hasScores && (
                <span
                  style={{
                    marginLeft: 'auto',
                    color: p.score > 0 ? '#f2d9a0' : 'var(--text-tertiary)',
                    fontSize: 12,
                    fontWeight: 600,
                    fontVariantNumeric: 'tabular-nums',
                  }}
                >
                  {p.score} pt{p.score === 1 ? '' : 's'}
                </span>
              )}
              {onKick && p.id !== myId && (
                <button
                  type="button"
                  onClick={() => onKick(p.id)}
                  aria-label={`Remove ${p.name}`}
                  title={`Remove ${p.name}`}
                  style={{
                    marginLeft: hasScores ? 'var(--space-2)' : 'auto',
                    flexShrink: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 28,
                    height: 28,
                    borderRadius: '50%',
                    border: '1px solid var(--surface-stroke)',
                    background: 'transparent',
                    color: 'var(--text-tertiary)',
                    cursor: 'pointer',
                    transition: 'all var(--duration-fast) var(--ease-standard)',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.color = 'var(--accent-blood-end)';
                    e.currentTarget.style.borderColor = 'var(--accent-blood-end)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.color = 'var(--text-tertiary)';
                    e.currentTarget.style.borderColor = 'var(--surface-stroke)';
                  }}
                >
                  <X size={15} strokeWidth={2.4} />
                </button>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
